import { getLogoutTokenEndpoint } from '@clients/api/auth';
import {
	COOKIES_PROFILE_HOST_KEY,
	LOCAL_STORAGE_PROFILE_HOST_KEY,
} from '@shared/constants/auth';
import { LOCAL_STORAGE_BYPASS_LIST } from '@shared/constants/local-storage';
import { PostMessageType } from '@shared/constants/notifications';
import { LOCAL_STORAGE_TABLE_PREFIX } from '@shared/constants/table';
import {
	getAuthority,
	getGlobalRefreshTokenKey,
	getMetadataUrl,
	getSessionOidcAuthKey,
} from '@shared/utils/auth';
import { deserializeLocalStorageValue } from '@shared/utils/localStorage';
import Cookies from 'js-cookie';
import compact from 'lodash/compact';
import delay from 'lodash/delay';
import { WebStorageStateStore } from 'oidc-client-ts';
import type { AuthProviderProps } from 'react-oidc-context';

export {
	COOKIES_PROFILE_HOST_KEY,
	LOCAL_STORAGE_PROFILE_HOST_KEY,
} from '@shared/constants/auth';

export const removeCaches = () => {
	sessionStorage.clear();
	console.debug('Removed Session Storage');

	const localStorageBypassList = [
		...LOCAL_STORAGE_BYPASS_LIST,
		LOCAL_STORAGE_TABLE_PREFIX,
		LOCAL_STORAGE_PROFILE_HOST_KEY,
		'custom-theme',
	];

	Object.keys(localStorage).forEach((key) => {
		if (localStorageBypassList.find((item) => key.includes(item))) {
			return;
		}
		localStorage.removeItem(key);
	});

	// localStorage.clear();
	console.debug('Removed Local Storage');

	const cookiesBypassList = new Set([COOKIES_PROFILE_HOST_KEY]);

	Object.keys(Cookies.get()).forEach((name) => {
		if (cookiesBypassList.has(name)) {
			console.debug('[Bypass Cookies Removal]', name);
			return;
		}
		Cookies.remove(name, { path: import.meta.env.VITE_BASE_NAME || '' });
	});
	console.debug('Removed Cookies');
};

export const unsubscribeNotification = async (mobileFcmToken?: string) => {
	const { revokeToken } = await import('@shared/firebase');
	/* Delete firebase messaging token */
	await revokeToken(mobileFcmToken);
};

export const signoutFallback = (pathname = location.pathname) => {
	removeCaches();
	if (window?.ReactNativeWebView?.postMessage) {
		window?.ReactNativeWebView?.postMessage?.(
			JSON.stringify({
				type: PostMessageType.RELOAD,
				data: {
					delay: 0,
				},
			})
		);
	} else {
		location.href = pathname;
	}
};

const onSigninCallback = async () => {
	await new Promise((resolve) => delay(resolve, 800));

	/* Remove redirect query params from keycloak */
	const authParams = new URLSearchParams(location.search);
	authParams.delete('iss');
	authParams.delete('code');
	authParams.delete('state');
	authParams.delete('session_state');

	const url = compact([location.pathname, authParams.toString()]).join('?');
	history.replaceState({}, document.title, url);
};

const onRemoveUser = async () => {
	try {
		/* Logout keycloak session */
		const sessionOidcAuthKey = getSessionOidcAuthKey();
		const globalRefreshTokenKey = getGlobalRefreshTokenKey();

		const refreshToken =
			deserializeLocalStorageValue<Record<string, unknown>>(sessionOidcAuthKey)
				?.refresh_token ||
			deserializeLocalStorageValue<Record<string, unknown>>(
				globalRefreshTokenKey
			);

		if (refreshToken) {
			await getLogoutTokenEndpoint({
				body: {
					client_id: import.meta.env.VITE_AUTH_CLIENT_ID as string,
					refresh_token: refreshToken as string,
				},
			});
		}
	} catch (e) {
		console.error('[OIDC] onRemoveUser Error', e);
	} finally {
		/* Remove all localStorage, sessionStorage and cookies */
		removeCaches();
	}
};

export const getDefaultAuthConfigs = (domain?: string): AuthProviderProps => {
	const authority = getAuthority(domain);
	const metadataUrl = getMetadataUrl(domain);

	return {
		authority,
		metadataUrl,
		client_id: import.meta.env.VITE_AUTH_CLIENT_ID,
		redirect_uri: `${location.protocol}//${location.host}${location.pathname}${location.search}`,
		post_logout_redirect_uri: `${location.protocol}//${location.host}${location.pathname}`,
		scope: 'openid profile email',
		automaticSilentRenew: true,
		revokeTokensOnSignout: true,
		userStore: new WebStorageStateStore({ store: window.localStorage }),
		onSigninCallback,
		onRemoveUser,
	};
};
